import { parse as parseCookies } from 'cookie';
import querystring from 'query-string';
import { lang as cfgLang } from 'config';

class RequestError extends Error {
    constructor(status = 500, data = {}) {
        super(`Error ${status}`);
        this.status = status;
        this.data = data;
    }
    status = null;
}

class Request {
    _method = 'get';
    _headers = {};
    _data = {};
    _response = null;
    _form = false;
    _language = 'en';

    constructor(method, config = {}) {
        this._method = method.toLowerCase();
        if (config && config.uri) this._uri = config.uri;
        if (config && config.data) this._data = config.data;
        if (config && config.form) this._form = config.form;
        if (config && config.language) this._language = config.language;
        if (config && config.headers) this._headers = config.headers;
        this._redirect = config && typeof config.redirect == 'undefined' ? true : config.redirect;
    }

    static get(config) {
        return new Request('get', config);
    }

    static post(config) {
        return new Request('post', config);
    }

    static put(config) {
        return new Request('put', config);
    }

    static patch(config) {
        return new Request('patch', config);
    }

    static delete(config) {
        return new Request('delete', config);
    }

    header(key, value) {
        this._headers[key] = value;
        return this;
    }

    getResponse() {
        return this._response;
    }

    parseToFormData(data) {
        const urlEncodedDataPairs = [];
        let name;
        for (name in data) {
            urlEncodedDataPairs.push(`${encodeURIComponent(name)}=${encodeURIComponent(data[name])}`);
        }
        return urlEncodedDataPairs.join('&').replace(/%20/g, '+');
    }

    execute(raw = false) {
        /*
        const locale = 'en';
        if (locale) this.header('Accept-Language', locale);
        */

        return this._exec(raw);
    }

    _exec(raw = false) {

        const dataSend = ['post', 'put', 'patch'].includes(this._method.toLowerCase());
        const dataCookie = ['post', 'put', 'patch', 'delete'].includes(this._method.toLowerCase());
        const paramSend = ['get'].includes(this._method.toLowerCase());

        // Target URI
        const queryString = paramSend && this._data && Object.keys(this._data).length > 0
            ? ((this._uri.match(/\?/) ? '&' : '?').concat(querystring.stringify(this._data)))
            : '';

        const uri =  `${this._uri}${queryString}`;

        // Status check callback
        const checkStatus = (response) => {
            if (response.status >= 200 && response.status < 300) {
                return response;
            }

            if (response.status === 401 && this._redirect) {
                window.location.replace('/login');
            }

            return response.text()
                .then((responseError) => {
                    let error;
                    try {
                        error = JSON.parse(responseError);
                    } catch (e) {
                        error = responseError;
                    }
                    return new Promise((resolve) => {
                        resolve(new RequestError(response.status, error));
                    });
                });

        };

        if (this._form) {
            this.header('Content-Type', 'application/x-www-form-urlencoded; charset=UTF-8');
            this.header('X-Redirect-To', this._data && this._data.url ? this._data.url : '');
            this.header('X-Requested-With', 'XMLHttpRequest');
            this.header('Accept-Language', cfgLang);
        } else {
            // Add json header in post/put/patch methods
            if (dataSend) {
                this.header('Content-Type', 'application/json; charset=utf-8');
            }
        }

        const csrf = parseCookies(document.cookie)['XSRF-TOKEN'];
        if (dataCookie && csrf) {
            this.header('X-XSRF-TOKEN', csrf);
        }

        const invcode = parseCookies(document.cookie)['dx.inv.code'];
        if (dataCookie && invcode) {
            this.header('X-DX-Invitation-Code', invcode);
        }

        const reff = parseCookies(document.cookie).__reff;
        if (dataCookie && reff) {
            this.header('X-DX-Referrer-User', reff);
        }

        const config = {
            method: this._method.toUpperCase(),
            headers: new Headers(this._headers),
            mode: 'cors',
            redirect: 'follow',
            credentials: 'include',
            cache: 'no-cache',
            body: (dataSend
                && this._data !== null
                && typeof this._data !== 'undefined'
                && typeof this._data === 'object'
                && JSON.stringify(this._data) !== JSON.stringify({})
                && !this._form)
                ? JSON.stringify(this._data)
                : this._form ? this.parseToFormData(this._data)
                : undefined
        };

        const request = fetch(uri, config)
            .then((response) => {
                this._response = response;
                return response;
            })
            .then(checkStatus);

        if (raw) {
            return request;
        }

        // Parses the JSON
        return request
            .then((response) => {
                if (response.status < 200 || response.status > 300) {
                    return Promise.reject({
                        data: response.data,
                    });
                }
                if (response.status === 204) {
                    return response.text();
                }
                return response.json();
            })
            .then(data => ({
                status: this.getResponse().status,
                statusText: this.getResponse().statusText,
                headers: ((headers) => {
                    const collection = {};
                    if (headers.keys) {
                        /* eslint-disable no-restricted-syntax */
                        for (const key of headers.keys()) {
                            collection[key] = headers.get(key);
                        }
                    } else if (headers.forEach) {
                        // Edge uses this
                        headers.forEach((value, key) => (collection[key] = value));
                    } else {
                        throw new Error('Can\'t retrieve headers from response');
                    }
                    return collection;
                })(this.getResponse().headers),
                data,
            }));
    }
}

export default Request;
